import { type JSX, useMemo } from 'react';

import { Button, Link } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { Translate } from '@change-corgi/core/react/i18n';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from 'src/app/pages/petitionDetails/pageContext';
import { useLoginStateAsync } from 'src/app/shared/hooks/session';
import { isLoaded } from 'src/app/shared/utils/async';

import { usePetitionStarterMediaInquiry } from '../../hooks/petitionStarterMediaInquiry';
import { useCommunityPetitionLinkTracking } from '../../hooks/tracking/communityPetitionLink';

import { AdvocateAvatar } from './components/AdvocateAvatar';
import { AdvocateInfo } from './components/AdvocateInfo';
import { SignButton } from './components/SignButton';
import { HAS_ADVOCATES_STATE } from './util';

export function CommunityPetitionLink(): JSX.Element {
	const {
		data: { userId, signed },
	} = usePetitionDetailsPageUserData();

	const {
		data: {
			petitionAdvocates,
			petition: { slug, id },
		},
	} = usePetitionDetailsPageData();

	const {
		data: { enabled, url },
		actions: { handleMediaInquiryClick },
	} = usePetitionStarterMediaInquiry(id);

	const { trackShareClick, trackJoinTeamClick, trackSignAdvocateClick } = useCommunityPetitionLinkTracking();

	const loginState = useLoginStateAsync();
	const loginStateLoaded = isLoaded(loginState);
	const isGuest = loginStateLoaded && loginState.value === 'GUEST';

	const isAdvocate = useMemo(() => {
		return petitionAdvocates?.some((advocate) => advocate?.id.toString() === userId);
	}, [userId, petitionAdvocates]);

	const hasAdvocatesState = useMemo(() => {
		if (!petitionAdvocates) return HAS_ADVOCATES_STATE.none;

		switch (petitionAdvocates.length) {
			case 0:
				return HAS_ADVOCATES_STATE.none;
			case 1:
				return HAS_ADVOCATES_STATE.one;
			default:
				return HAS_ADVOCATES_STATE.multiple;
		}
	}, [petitionAdvocates]);

	return (
		<div className="mt-4 flex justify-between gap-2 border-t border-t-stroke-weaker pt-6">
			<div
				className={cn('flex items-center gap-3', {
					'flex-col items-start gap-2': hasAdvocatesState === HAS_ADVOCATES_STATE.multiple,
				})}
			>
				<AdvocateAvatar hasAdvocatesState={hasAdvocatesState} petitionAdvocates={petitionAdvocates} />
				<AdvocateInfo hasAdvocatesState={hasAdvocatesState} petitionAdvocates={petitionAdvocates} />
			</div>
			<div className="flex shrink-0 flex-col items-end justify-start gap-3">
				{signed ? (
					<Button
						as={Link}
						href={isAdvocate ? `/p/${slug}/psf/promote_or_share` : `/p/${slug}/advocates`}
						variant="secondary"
						size="small"
						data-qa="advocate-button"
						underline={false}
						className="!text-typography-strong"
						onClick={isAdvocate ? trackShareClick : trackJoinTeamClick}
					>
						{isAdvocate ? 'Share' : 'Join the team'}
					</Button>
				) : (
					loginStateLoaded && (
						<SignButton
							isGuest={isGuest}
							id={id}
							slug={slug}
							onClick={trackSignAdvocateClick}
							data-qa="sign-advocate-button"
						/>
					)
				)}
				{enabled && (
					<Link
						href={url}
						variant="primary"
						className="text-typography-strong"
						data-testid="media-inquiry-link"
						data-qa="media-inquiry-link"
						onClick={handleMediaInquiryClick}
					>
						<Translate value="corgi.petition_gamma.petition_starter.add_media_inquiries.action" />
					</Link>
				)}
			</div>
		</div>
	);
}
